import type {
  Binary,
  ColumnRefItem,
  ExpressionValue,
  ExprList,
  Function,
  ValueExpr as NodeSqlParserValueExpr,
  Select,
} from 'node-sql-parser';

type ValueExprType = NodeSqlParserValueExpr['type'] | 'number' | 'bigint';

export type ASTNode = Binary | Function | Select | ValueExpr | Binary | Function | null | ExpressionValue | ExprList;
export type Operators = NumberOperator | StringOperator | BooleanOperator | DateOperator;
export type Values = number | string | boolean | null;
export type ValueExprList = { type: 'expr_list'; value: ValueExpr[] };

export const getColumnName = (node: ColumnRefItem & { db?: string | null }): string => {
  const column = String(typeof node.column === 'string' ? node.column : node.column.expr.value);
  return [node.db, node.table, column].filter(Boolean).join('.');
};

export const isBinaryExpr = (node: ASTNode): node is Binary => node?.type === 'binary_expr';
export const isValueExprList = (node: ASTNode): node is ValueExprList =>
  node?.type === 'expr_list' && Array.isArray(node.value) && node.value.every(isValueExpr);

export const isAndOperator = (op: string): op is 'AND' => op === 'AND';
export const isOrOperator = (op: string): op is 'OR' => op === 'OR';
export const isAndOrOperator = (op: string): op is 'AND' | 'OR' => isAndOperator(op) || isOrOperator(op);
export const isColumnRefItem = (node: ExpressionValue): node is ColumnRefItem => node.type === 'column_ref';

const supportedValueTypes = [
  'backticks_quote_string',
  'double_quote_string',
  'single_quote_string',
  'string',
  'boolean',
  'bool',
  'null',
  'number',
  'date',

  // TODO: Add bigint tests @nizans
  'bigint',
] as const satisfies Array<ValueExprType>;

export type SupportedValueType = (typeof supportedValueTypes)[number];

const isValueExprType = (type: string): type is (typeof supportedValueTypes)[number] =>
  supportedValueTypes.includes(type as (typeof supportedValueTypes)[number]);

export const isValueExpr = (node: unknown): node is ValueExpr =>
  Boolean(node && typeof node === 'object' && 'type' in node && typeof node.type === 'string' && isValueExprType(node.type));

const nullOperators = ['IS', 'IS NOT'] as const;
export type NullOperator = (typeof nullOperators)[number];
export const isNullOperator = (op: string): op is (typeof nullOperators)[number] =>
  nullOperators.includes(op as (typeof nullOperators)[number]);

const exprListOperator = ['IN', 'NOT IN', 'BETWEEN'] as const;
export type ExprListOperator = (typeof exprListOperator)[number];
export const isExprListOperator = (op: string): op is ExprListOperator => exprListOperator.includes(op as ExprListOperator);

const numberOperators = ['>', '<', '>=', '<=', '=', '!=', 'BETWEEN'] as const;
export type NumberOperator = (typeof numberOperators)[number];
export const isNumberOperator = (op: string): op is NumberOperator => numberOperators.includes(op as NumberOperator);

const stringOperators = ['=', '!=', 'LIKE', 'NOT LIKE', 'IN', 'NOT IN'] as const;
export type StringOperator = (typeof stringOperators)[number];
export const isStringOperator = (op: string): op is StringOperator => stringOperators.includes(op as StringOperator);

const booleanOperators = ['=', '!='] as const;
export type BooleanOperator = (typeof booleanOperators)[number];
export const isBooleanOperator = (op: string): op is BooleanOperator => booleanOperators.includes(op as BooleanOperator);

const dateOperators = ['BETWEEN'] as const;
export type DateOperator = (typeof dateOperators)[number];
export const isDateOperator = (op: string): op is DateOperator => dateOperators.includes(op as DateOperator);

export const isOperator = (op: string): op is NumberOperator | StringOperator | BooleanOperator =>
  isNumberOperator(op) || isStringOperator(op) || isBooleanOperator(op) || isNullOperator(op) || isDateOperator(op);

export const isValidContainsValue = (value: string): value is `%${string}%` =>
  value.startsWith('%') && value.endsWith('%') && value.length >= 2;
export const isValidNotContainsValue = (value: string): value is `%${string}%` => isValidContainsValue(value);
export const isValidStartWithValue = (value: string): value is `%${string}` =>
  (!value.startsWith('%') || value.length === 1) && value.endsWith('%');
export const isValidEndWithValue = (value: string): value is `${string}%` =>
  value.startsWith('%') && (!value.endsWith('%') || value.length === 1);

export const cleanLikeValue = (value: string): string => value.replace(/(^%+|%+$)/g, '').replace(/%+/g, '%');

type NumberValueExpr = { type: Extract<SupportedValueType, 'number' | 'bigint'>; value: number };
type StringValueExpr = {
  type: Extract<SupportedValueType, 'string' | 'backticks_quote_string' | 'double_quote_string' | 'single_quote_string'>;
  value: string;
};
type BooleanValueExpr = { type: Extract<SupportedValueType, 'boolean' | 'bool'>; value: boolean };
type NullValueExpr = { type: Extract<SupportedValueType, 'null'>; value: null };
type DateValueExpr = { type: Extract<SupportedValueType, 'date'>; value: string };
export type ValueExpr = NumberValueExpr | StringValueExpr | BooleanValueExpr | NullValueExpr | DateValueExpr;

const supportedFunctions = ['RELATIVE_DATE'] as const;
export type SupportedFunction = (typeof supportedFunctions)[number];
export const isSupportedFunction = (functionName: string): functionName is SupportedFunction =>
  supportedFunctions.includes(functionName as SupportedFunction);

export const isFunctionNode = (node: ASTNode): node is Function => node?.type === 'function';
export const getFunctionName = (functionNode: Function): string => {
  const { name } = functionNode.name;
  const [{ value }] = name;
  return value;
};

export const getFunctionArguments = ({ args }: Function): ValueExpr[] =>
  args?.value.map(arg => {
    if (isValueExpr(arg)) {
      return arg;
    }
    throw new Error(`Unsupported function argument type: ${arg.type}`);
  }) || [];
